import { useCallback, useEffect, useState } from "react";
import { KeyRound, Power } from "lucide-react";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { ConfirmDialog } from "./AdminModals";
import { Skeleton } from "../ui/Skeleton";
import { StatusMessage, mapApiError } from "../ui/StatusMessage";
import { useToast } from "../ui/Toast";
import { api } from "../../lib/api";
import type { IngestionAuditItem, IngestionState } from "../../lib/chaptersApi";

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Admin: ingestion switch with countdown, key, limits and audit trail. */
export function IngestionSettingsSection({ token }: { token: string }) {
  const { showToast } = useToast();
  const [state, setState] = useState<IngestionState | null>(null);
  const [audit, setAudit] = useState<IngestionAuditItem[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [confirmKey, setConfirmKey] = useState(false);
  const [minutes, setMinutes] = useState("");
  const [minQuestions, setMinQuestions] = useState("");
  const [allowlist, setAllowlist] = useState("");

  const apply = (s: IngestionState) => {
    setState(s);
    setSecondsLeft(s.seconds_left);
    setMinutes(String(s.auto_off_minutes));
    setMinQuestions(String(s.min_questions_per_difficulty));
    setAllowlist(s.ip_allowlist.join(", "));
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      apply(await api.admin.chapters.ingestion.get(token));
      setAudit((await api.admin.chapters.ingestion.audit(token)).items);
    } catch (err) {
      setError(err);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  // Local countdown; the server is the authority and turns the switch off by itself.
  useEffect(() => {
    if (!state?.enabled) return;
    const id = window.setInterval(() => {
      setSecondsLeft((v) => {
        if (v <= 1) {
          window.clearInterval(id);
          void load();
          return 0;
        }
        return v - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [state?.enabled, load]);

  const act = async (fn: () => Promise<IngestionState>, ok: string) => {
    setBusy(true);
    try {
      apply(await fn());
      showToast("success", ok);
      setAudit((await api.admin.chapters.ingestion.audit(token)).items);
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const saveConfig = () =>
    act(
      () =>
        api.admin.chapters.ingestion.config(token, {
          auto_off_minutes: Number(minutes) || undefined,
          min_questions_per_difficulty: Number(minQuestions) || undefined,
          ip_allowlist: allowlist.split(",").map((x) => x.trim()).filter(Boolean),
        }),
      "Ingestion settings saved",
    );

  const createKey = async () => {
    setConfirmKey(false);
    setBusy(true);
    try {
      const res = await api.admin.chapters.ingestion.newKey(token);
      setNewKey(res.key);
      apply(res);
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    const m = mapApiError(error);
    return <StatusMessage variant={m.variant} title={m.title} message={m.message} action={{ label: "Try again", onClick: () => void load() }} />;
  }
  if (!state) return <Skeleton className="h-40 w-full" />;

  return (
    <section className="space-y-4" aria-labelledby="ingestion-heading">
      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 id="ingestion-heading" className="text-lg font-semibold">Content ingestion</h2>
            <p className="text-sm text-muted-foreground">
              Lets your local script send chapters. Everything it sends is saved as a draft until you publish it.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {state.enabled ? <Badge variant="success">On · {formatCountdown(secondsLeft)} left</Badge> : <Badge variant="secondary">Off</Badge>}
            {state.enabled ? (
              <Button variant="outline" icon={<Power size={16} />} loading={busy} onClick={() => void act(() => api.admin.chapters.ingestion.disable(token), "Ingestion turned off")}>
                Turn off
              </Button>
            ) : (
              <Button
                icon={<Power size={16} />}
                loading={busy}
                disabled={!state.has_key}
                onClick={() => void act(() => api.admin.chapters.ingestion.enable(token, Number(minutes) || undefined), "Ingestion turned on")}
              >
                Turn on
              </Button>
            )}
          </div>
        </div>
        {!state.has_key && <p className="text-sm text-muted-foreground">Create a key first, then turn ingestion on.</p>}
      </Card>

      <Card className="space-y-3 p-4">
        <h3 className="text-base font-semibold">Key</h3>
        <p className="text-sm text-muted-foreground">
          {state.has_key ? `Current key starts with ${state.key_prefix}.` : "No key yet."} Only a hash is stored, so a key can be shown once.
        </p>
        {newKey && (
          <div role="status" className="rounded-md border border-border bg-muted p-3">
            <p className="text-sm font-medium">Copy this key now. It will not be shown again.</p>
            <code className="mt-1 block break-all text-sm">{newKey}</code>
          </div>
        )}
        <Button variant="outline" icon={<KeyRound size={16} />} onClick={() => setConfirmKey(true)} loading={busy}>
          {state.has_key ? "Replace key" : "Create key"}
        </Button>
      </Card>

      <Card className="space-y-3 p-4">
        <h3 className="text-base font-semibold">Limits</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="font-medium">Turn off automatically after (minutes)</span>
            <input className="min-h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:text-sm" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Minimum questions per level</span>
            <input className="min-h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:text-sm" inputMode="numeric" value={minQuestions} onChange={(e) => setMinQuestions(e.target.value)} />
          </label>
        </div>
        <label className="block space-y-1 text-sm">
          <span className="font-medium">Allowed IP addresses (optional, comma separated)</span>
          <input className="min-h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:text-sm" value={allowlist} onChange={(e) => setAllowlist(e.target.value)} placeholder="Leave empty to allow any address" />
        </label>
        <Button loading={busy} loadingText="Saving…" onClick={() => void saveConfig()}>
          Save limits
        </Button>
      </Card>

      <Card className="space-y-2 p-4">
        <h3 className="text-base font-semibold">Recent requests</h3>
        {audit.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No requests yet.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {audit.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span>{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
                <span className="flex items-center gap-2">
                  <Badge variant={a.outcome === "accepted" || a.outcome === "dry_run" ? "success" : "warning"}>{a.outcome.replace("_", " ")}</Badge>
                  <span>{a.chapter_count} chapters</span>
                  <span className="text-muted-foreground">{a.ip}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ConfirmDialog
        isOpen={confirmKey}
        title={state.has_key ? "Replace the ingestion key?" : "Create an ingestion key?"}
        message={state.has_key ? "The old key stops working immediately and ingestion is turned off." : "You will see the key once."}
        confirmText={state.has_key ? "Replace key" : "Create key"}
        variant="primary"
        loading={busy}
        onConfirm={() => void createKey()}
        onClose={() => setConfirmKey(false)}
      />
    </section>
  );
}
